/**
 * 動画メタ化の縦の導線（見積もり → 確認 → 起動 → 進み具合）。
 *
 * この面が守っていること:
 * - **見積もりを見ずに起動できる経路を作らない。** 開始に渡す対象件数は、
 *   見積もりが返した `targetCount` をそのまま渡す（画面が数え直さない）。
 *   食い違えば API が `CONFLICT` で断り、その断りをそのまま出す。
 * - **費用は通貨つきで出し、1 本あたりの内訳を額と同じ場所に置く。**
 *   内訳はサーバーが作った文面（`basis.note`）をそのまま出す — 画面が
 *   単価を持つと、サーバーの単価が変わったときに 2 つの数字がずれる。
 * - **所要時間は出さない。** 1 度も流していないので測れていない
 *   （`estimatedSeconds` は null で返る。当てずっぽうの秒数を置かない）。
 * - **進み具合は台帳から引き直す。** jobId を端末の中だけに覚えると、
 *   別の端末・別のタブから続きが見えなくなる。
 * - 引き直す間隔は `Job.suggestedPollIntervalSec` に従う。画面が秒数を
 *   決め打ちしない。null（終端）になったらポーリングを止める。
 * - 失敗・対象外・次回送りを黙って数から消さない。
 * - **処理済みの動画は一括の対象に入らない。** 対象の本数は未処理のぶんで、
 *   外した処理済みの本数を別の行で出す（読み直しは 1 本ずつ名指しで）。
 *   数え切れなかったときは「以上」と出す（数えていない先を言い切らない）。
 * - 指定の本数に届かずに始まったときは、そう言う（黙って少なく始めない）。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  Disclosure,
  ErrorState,
  LoadingState,
  Meter,
  RunningVideoMetaJobsDocument,
  StartVideoMetaBatchDocument,
  Tag,
  VideoMetaBatchEstimateDocument,
  type JobEstimateFieldsFragment,
  type JobProgressFieldsFragment,
  type JobState,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import { JobControls } from "./JobControls";

/** 終端。ここに来たらポーリングを止める。 */
const TERMINAL_STATES: readonly JobState[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

/**
 * 一度に流せる本数の選択肢。**SDL の上限（50）を超える値を出さない。**
 * 既定は 20 で、`StartVideoMetaBatchInput.limit` の既定と同じ値にしてある。
 */
const BATCH_LIMITS = [10, 20, 50] as const;
const DEFAULT_BATCH_LIMIT = 20;

function isTerminal(state: JobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

/** 1 起動につき 1 つ。再送のたびに作り直すと二重に起動できてしまう。 */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * 金額は**通貨つき**で出す。通貨が付いていない額は、読む人の通貨で
 * 読まれてしまう（USD の 0.13 を 0.13 円と読める）。
 */
function money(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (amount == null) return null;
  return `${amount.toLocaleString("ja-JP", { maximumFractionDigits: 4 })} ${currency ?? ""}`.trim();
}

/**
 * 一括の見積もりにだけある、対象から外した処理済みの本数。
 * ジョブの見込みでは null（数えていない）で、そのときは何も出さない。
 */
type BatchEstimateFields = JobEstimateFieldsFragment & {
  readonly processedCount?: number | null;
  readonly processedCountExact?: boolean | null;
};

/**
 * 見積もりの中身。費用が出せないときは、出せない理由を数字と同じ場所に置く
 * （空欄にすると「0 円」と読めてしまう）。
 */
export function VideoMetaEstimateBody({
  estimate,
}: {
  estimate: BatchEstimateFields;
}) {
  const cost = money(estimate.estimatedCost, estimate.estimatedCurrency);
  return (
    <>
      <div className="vd-grid">
        <div>
          <div className="vd-stat-label">処理できる動画</div>
          <div className="vd-stat-value" aria-label="対象の本数">
            {estimate.targetCount.toLocaleString("ja-JP")} 本
          </div>
          <div className="vd-stat-note">{estimate.basis.label}</div>
          {estimate.processedCount == null ||
          estimate.processedCount === 0 ? null : (
            <div className="vd-stat-note" aria-label="処理済みの本数">
              処理済み {count(estimate.processedCount)} 本
              {estimate.processedCountExact === false ? "以上" : ""}
              は一括の対象に入りません（1
              本ずつ「もう一度処理する」で再処理できます）
            </div>
          )}
        </div>
        <div>
          <div className="vd-stat-label">AI への問い合わせ</div>
          <div className="vd-stat-value">
            {count(estimate.estimatedLlmCalls)} 回
          </div>
          <div className="vd-stat-note">予算はこの回数で数えています</div>
        </div>
        <div>
          <div className="vd-stat-label">費用の見込み</div>
          <div className="vd-stat-value" aria-label="費用の見込み">
            {cost ?? "出していません"}
          </div>
          <div className="vd-stat-note">
            {cost === null
              ? (estimate.costGap?.why ?? "単価の記録がありません")
              : "実行後の請求で確定します"}
          </div>
          <div className="vd-stat-note">
            未処理の本数 × 平均の尺で見込んでいます
          </div>
        </div>
        <div>
          <div className="vd-stat-label">終わるまでの時間</div>
          <div className="vd-stat-value" aria-label="終わるまでの時間">
            出していません
          </div>
          <div className="vd-stat-note">
            まだ 1 度も流していないので、測ってから出します
          </div>
        </div>
      </div>

      {/* 額と内訳を切り離さない。1 本あたりの内訳はここに入っている */}
      <p className="vd-stat-note" aria-label="費用の内訳">
        {estimate.basis.note}
      </p>

      {estimate.skippedCount === 0 ? (
        <p className="vd-stat-note" style={{ marginBottom: 0 }}>
          当面の対象外にあたる動画は、数えた範囲ではありませんでした。
        </p>
      ) : (
        <p className="vd-stat-note" aria-label="対象外の内訳">
          このうち {count(estimate.skippedCount)} 本は当面の対象外です（
          {estimate.skippedReasons
            .map((item) => `${item.label} ${count(item.count)} 本`)
            .join(" ・ ")}
          ）。
        </p>
      )}
    </>
  );
}

/** 進み具合の面。state ごとに言うことを変える。 */
function VideoJobView({ job }: { job: JobProgressFieldsFragment }) {
  const results = (
    <div className="vd-grid">
      <div>
        <div className="vd-stat-label">読み取れた</div>
        <div className="vd-stat-value">{count(job.succeeded)} 本</div>
      </div>
      <div>
        <div className="vd-stat-label">読み取れなかった</div>
        <div className="vd-stat-value">{count(job.failed)} 本</div>
      </div>
      <div>
        <div className="vd-stat-label">対象外</div>
        <div className="vd-stat-value">{count(job.skipped)} 本</div>
      </div>
      <div>
        <div className="vd-stat-label">次回に回った</div>
        <div className="vd-stat-value">{count(job.deferred)} 本</div>
      </div>
    </div>
  );

  const reasons =
    job.skippedReasons.length === 0 ? null : (
      <ul className="vd-job-reasons" aria-label="対象外になった理由">
        {job.skippedReasons.map((item) => (
          <li key={item.reason}>
            {item.label}: {count(item.count)} 本
          </li>
        ))}
      </ul>
    );

  switch (job.state) {
    case "QUEUED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            待ち行列にいます
            <Tag tone="info">受付</Tag>
          </div>
          <p className="vd-stat-note">
            順番が来ると読み取りを始めます。この画面を閉じても進みます。
          </p>
          {job.estimate === null || job.estimate === undefined ? null : (
            <VideoMetaEstimateBody estimate={job.estimate} />
          )}
        </div>
      );

    case "RUNNING":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            動画を読み取っています（{count(job.succeeded)} / {count(job.total)}
            ）<Tag tone="info">実行中</Tag>
          </div>
          <Meter ratio={job.progress} />
          <p className="vd-stat-note">
            {job.progress == null
              ? "進み具合はまだ出せません"
              : `${String(Math.round(job.progress * 100))} % まで進みました`}
            。この画面を閉じても進みます。
          </p>
        </div>
      );

    case "SUCCEEDED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            動画の読み取りが終わりました
            <Tag tone="ok">完了</Tag>
          </div>
          {results}
          {reasons}
        </div>
      );

    case "SUCCEEDED_WITH_ERRORS":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            一部の動画を読み取れませんでした
            <Tag tone="warn">一部失敗</Tag>
          </div>
          {results}
          {(job.deferred ?? 0) > 0 ? (
            <p style={{ margin: "8px 0 0" }}>
              この回の上限に達したぶんは次回に回りました。もう一度始めると続きから
              処理します。
            </p>
          ) : null}
          {reasons}
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            読み取れなかった動画は、1 本ずつ開くと理由が出ます。
          </p>
        </div>
      );

    case "FAILED":
      return (
        <div className="vd-job vd-job--error" role="alert">
          <div className="vd-card-title">
            動画の読み取りに失敗しました
            <Tag tone="danger">失敗</Tag>
          </div>
          <p style={{ margin: "4px 0 0" }} aria-label="失敗の理由">
            {job.errorSummary ?? "理由を受け取れませんでした。"}
          </p>
          {results}
        </div>
      );

    case "CANCELLED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            動画の読み取りは中止されました
            <Tag>中止</Tag>
          </div>
          {results}
        </div>
      );
  }
}

export function VideoMetaBatchPanel({
  onFinished,
}: {
  /** 終わったら一覧を引き直す（読み取れた動画の状態を反映するため） */
  onFinished: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [limit, setLimit] = useState<number>(DEFAULT_BATCH_LIMIT);
  /** 始めたときに予定した本数。届かずに始まったかを言うために控える。 */
  const [startedLimit, setStartedLimit] = useState<number | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [controlled, setControlled] =
    useState<JobProgressFieldsFragment | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const idempotencyKey = useRef<string | null>(null);
  const notified = useRef<string | null>(null);

  const [estimateResult, refetchEstimate] = useQuery({
    query: VideoMetaBatchEstimateDocument,
  });

  // 画面を開き直したときに拾い直すぶん。端末の中だけに覚えない
  const [runningResult] = useQuery({
    query: RunningVideoMetaJobsDocument,
    variables: { state: "RUNNING" as JobState },
    pause: jobId !== null,
  });
  const [queuedResult] = useQuery({
    query: RunningVideoMetaJobsDocument,
    variables: { state: "QUEUED" as JobState },
    pause: jobId !== null,
  });

  // 進み具合は、起動したあとも**台帳から**引き直す
  const [progressResult, refetchProgress] = useQuery({
    query: RunningVideoMetaJobsDocument,
    variables: { state: "RUNNING" as JobState },
    pause: true,
  });

  const [startResult, startBatch] = useMutation(StartVideoMetaBatchDocument);

  const startedJob = startResult.data?.startVideoMetaBatch.job ?? null;
  const foundJob =
    runningResult.data?.jobs.edges[0]?.node ??
    queuedResult.data?.jobs.edges[0]?.node ??
    null;
  const polledJob =
    progressResult.data?.jobs.edges.find((edge) => edge.node.id === jobId)
      ?.node ?? null;
  /**
   * 取り消し・やり直しが返した Job。
   *
   * 一覧の問い合わせは「動いているもの」しか返さないので、取り消した直後の
   * 姿はそこからは引けない。**押した結果をそのまま出す**ために、次に引き直す
   * までのあいだだけここを優先する。
   */
  const job =
    (controlled?.id === jobId ? controlled : null) ??
    polledJob ??
    startedJob ??
    foundJob;

  /**
   * 拾い直しの問い合わせがまだ解決していない。
   *
   * **この間は起動の導線を出さない。** すでに動いている処理があった場合に
   * 「押せる状態 → 進み具合」と一瞬で入れ替わり、通しが解決前の姿を見て
   * 「始められる」と読み違えるため（FAQ 生成で実際に起きた）。
   */
  const lookingUp =
    jobId === null &&
    (runningResult.data === undefined || queuedResult.data === undefined) &&
    runningResult.error === undefined &&
    queuedResult.error === undefined;

  // 動いているものがあれば、押していなくても進み具合の面へ戻す
  useEffect(() => {
    if (jobId !== null || foundJob === null) {
      return;
    }
    setJobId(foundJob.id);
    setConfirming(false);
  }, [jobId, foundJob]);

  // 次に引くまでの秒数はサーバーが指示する。null なら終端なので止める
  useEffect(() => {
    if (jobId === null || job === null) {
      return;
    }
    const intervalSec = job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      // 引き直したら台帳の姿が正本。押した結果の写しは捨てる
      setControlled(null);
      refetchProgress({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [jobId, job, pollTick, refetchProgress]);

  // 終わったら一覧と見積もりを引き直す。1 つのジョブにつき 1 回だけ
  useEffect(() => {
    if (job === null || !isTerminal(job.state) || notified.current === job.id) {
      return;
    }
    notified.current = job.id;
    refetchEstimate({ requestPolicy: "network-only" });
    onFinished();
  }, [job, onFinished, refetchEstimate]);

  const start = useCallback(
    async (estimate: JobEstimateFieldsFragment) => {
      idempotencyKey.current ??= newIdempotencyKey();
      const response = await startBatch({
        input: {
          // 画面が数え直さず、見積もりが返した件数をそのまま渡す
          acknowledgedTargetCount: estimate.targetCount,
          limit,
          idempotencyKey: idempotencyKey.current,
        },
      });
      const started = response.data?.startVideoMetaBatch.job ?? null;
      if (started !== null) {
        // サーバーが詰めるのは、指定と予算で流せる本数の小さいほうまで
        setStartedLimit(Math.min(limit, estimate.estimatedLlmCalls));
        setJobId(started.id);
        setConfirming(false);
        idempotencyKey.current = null;
      }
    },
    [limit, startBatch],
  );

  const reset = (): void => {
    setJobId(null);
    setConfirming(false);
    idempotencyKey.current = null;
    notified.current = null;
  };

  const userErrors = startResult.data?.startVideoMetaBatch.userErrors ?? [];
  const estimate = estimateResult.data?.videoMetaBatchEstimate ?? null;
  const canStart =
    estimate !== null && estimate.targetCount > 0 && estimate.costGap === null;

  return (
    <Card
      title="いま処理できる動画"
      note="読み取りは AWS の費用がかかります。押すとまず見積もりを確かめます。"
      extra={
        <span className="vd-row">
          {jobId === null && !confirming && !lookingUp && canStart ? (
            <Button
              variant="primary"
              onClick={() => {
                setConfirming(true);
              }}
            >
              処理を始める
            </Button>
          ) : null}
          <HelpIcon slug="video-meta/batch" />
        </span>
      }
    >
      {lookingUp ? (
        <LoadingState label="動いている処理があるか確かめています" />
      ) : jobId !== null ? (
        <div className="vd-stack">
          {progressResult.error !== undefined ? (
            <ErrorState
              title="進み具合を読み込めませんでした"
              description={progressResult.error.message}
              onRetry={() => {
                refetchProgress({ requestPolicy: "network-only" });
              }}
            />
          ) : job === null ? (
            <LoadingState label="進み具合を読み込んでいます" />
          ) : (
            <>
              {startedJob !== null &&
              startedJob.id === job.id &&
              startedLimit !== null &&
              (startedJob.total ?? 0) < startedLimit ? (
                <p className="vd-stat-note" aria-label="始めた本数">
                  予定の {count(startedLimit)} 本に届かず、
                  {count(startedJob.total)} 本で始めました（
                  {startedJob.estimate?.basis.label ??
                    "まだ処理していない動画がそれだけでした"}
                  ）。
                </p>
              ) : null}
              <VideoJobView job={job} />
              <JobControls
                job={job}
                onChanged={(next) => {
                  if (next.id !== job.id) {
                    notified.current = null;
                  }
                  setControlled(next);
                  setJobId(next.id);
                }}
              />
              {isTerminal(job.state) ? (
                <div className="vd-row">
                  <Button onClick={reset}>閉じる</Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : estimateResult.error !== undefined ? (
        <ErrorState
          title="見積もりを出せませんでした"
          description={estimateResult.error.message}
          onRetry={() => {
            refetchEstimate({ requestPolicy: "network-only" });
          }}
        />
      ) : estimate === null ? (
        <LoadingState label="見積もりを出しています" />
      ) : (
        <div className="vd-stack">
          <VideoMetaEstimateBody estimate={estimate} />

          {estimate.costGap === null ? null : (
            <Disclosure summary="費用の見込みを出せないのはなぜか">
              <p style={{ margin: 0 }}>
                {estimate.costGap.why}（必要なもの: {estimate.costGap.needs}）
              </p>
            </Disclosure>
          )}

          {estimate.targetCount === 0 ? (
            <p style={{ margin: 0 }} aria-label="始められない理由">
              いま処理できる動画がありません。
            </p>
          ) : null}

          {userErrors.length === 0 ? null : (
            <div className="vd-state vd-state--error" role="alert">
              <span className="vd-state-title">
                読み取りを始められませんでした
              </span>
              {userErrors.map((error) => (
                <span key={`${error.code}-${error.message}`}>
                  {error.message}
                </span>
              ))}
            </div>
          )}

          {confirming ? (
            <>
              <div className="vd-row">
                <label>
                  <span style={{ marginRight: 6 }}>一度に処理する本数</span>
                  <select
                    aria-label="一度に処理する本数"
                    value={String(limit)}
                    onChange={(event) => {
                      setLimit(Number(event.target.value));
                    }}
                  >
                    {BATCH_LIMITS.map((value) => (
                      <option key={value} value={String(value)}>
                        {value} 本まで
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="vd-stat-note" style={{ margin: 0 }}>
                対象の全件を 1 回で流しません。残りは、終わってからもう一度
                始めます。
              </p>
              <div className="vd-row">
                <Button
                  variant="primary"
                  disabled={startResult.fetching}
                  onClick={() => {
                    void start(estimate);
                  }}
                >
                  {startResult.fetching
                    ? "起動しています"
                    : "この内容で処理する"}
                </Button>
                <Button
                  onClick={() => {
                    setConfirming(false);
                  }}
                >
                  やめる
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}
